/**
 * Application state.
 *
 * One store rather than a state library: the app is a calendar with a handful
 * of collections, and every write goes through the API and comes back as the
 * new server truth, so a cache-plus-reducer is all the machinery it needs.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  addDays,
  civilToInstant,
  daysInMonth,
  HIJRI_MAX_YEAR,
  HIJRI_MIN_YEAR,
  instantToCivil,
  JALALI_MAX_YEAR,
  JALALI_MIN_YEAR,
  startOfMonth,
  today as todayIn,
  type CalendarEvent,
  type CalendarSystem,
  type Category,
  type CivilDate,
  type EventStatus,
  type Lang,
  type User,
} from "@calendar/shared";

import { api, clearCredentials, getCredentials } from "./api.js";
import { dateKey, monthGrid, rangeForDays, weekGrid } from "./calendar-grid.js";
import { desktopNotify } from "./desktop.js";
import { ui } from "./i18n.js";
import { eventsToIcs, parseIcs } from "./ics.js";

export type View = "year" | "month" | "week" | "list" | "search" | "settings";

export interface Toast {
  id: number;
  text: string;
  kind: "ok" | "error";
  vars?: Record<string, string | number>;
}

export interface AppState {
  ready: boolean;
  user: User | null;
  events: CalendarEvent[];
  categories: Category[];
  view: View;
  /** The day the month or week view is centred on. */
  anchor: CivilDate;
  /** Event being edited, or null when the editor is closed. */
  editing: CalendarEvent | "new" | null;
  /** Pre-fill for a freshly opened editor, e.g. the cell that was clicked. */
  editorSeed: { startUtc: string; day: CivilDate | null } | null;
  toasts: Toast[];
  noteResults: CalendarEvent[];
  query: string;
  loading: boolean;
}

let toastSeq = 0;

/**
 * The inclusive year range a calendar can actually convert.
 *
 * The year dropdown and the arrow keys both read this, so every year the user
 * can reach is one the converters will not throw on. Gregorian and Jalali share
 * a practical range because the Persian calendar is the one with hard limits;
 * Hijri is bounded by its own table.
 */
function yearRange(calendar: CalendarSystem): [number, number] {
  if (calendar === "hijri") return [HIJRI_MIN_YEAR, HIJRI_MAX_YEAR];
  if (calendar === "gregorian") {
    // The Jalali bounds are 1799-2254 CE, offset by the year the calendar
    // starts in rather than by a fixed number of years.
    return [1799, 2254];
  }
  return [JALALI_MIN_YEAR, JALALI_MAX_YEAR];
}

/** Language drives the document direction, so it is applied outside React too. */
function applyLanguage(lang: Lang) {
  document.documentElement.lang = lang;
  document.documentElement.dir = lang === "fa" ? "rtl" : "ltr";
  localStorage.setItem("sc.lang", lang);
}

export function useApp() {
  const [state, setState] = useState<AppState>({
    ready: false,
    user: null,
    events: [],
    categories: [],
    view: "year",
    anchor: { year: 1405, month: 1, day: 1 },
    editing: null,
    editorSeed: null,
    toasts: [],
    noteResults: [],
    query: "",
    loading: false,
  });

  // The user's zone, calendar and language are inputs to every date operation,
  // so they are resolved once and passed around explicitly.
  const timeZone = state.user?.timeZone ?? "Asia/Tehran";
  const calendar = state.user?.calendar ?? "jalali";
  const lang = state.user?.lang ?? "fa";

  // ---- bootstrap ------------------------------------------------------

  useEffect(() => {
    if (!getCredentials()) {
      setState((s) => ({ ...s, ready: true }));
      return;
    }
    void (async () => {
      try {
        const { user } = await api.me();
        setState((s) => ({
          ...s,
          user,
          anchor: todayIn(user.calendar, user.timeZone, new Date()),
          ready: true,
        }));
        await Promise.all([refreshCategories(), refreshEvents()]);
      } catch {
        clearCredentials();
        setState((s) => ({ ...s, ready: true }));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- data loading ---------------------------------------------------

  /** The day range the current view needs, as an inclusive-exclusive range. */
  const visibleDays = useMemo<CivilDate[]>(() => {
    if (state.view === "week") return weekGrid(state.anchor, calendar);
    if (state.view === "month" || state.view === 'list') return monthGrid(state.anchor, calendar);
    // The year overview has no single grid. Its range is built separately,
    // because it needs the first day of the year through the last day of the
    // year, not the twelve month grids put together.
    return [];
  }, [state.view, state.anchor, calendar]);

  /**
   * How many events fall on each day, keyed exactly as the calendar views key
   * their days. The year overview only needs the counts, so it reads this
   * rather than a second copy of the events.
   */
  const eventCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const event of state.events) {
      const start = instantToCivil(event.startUtc, timeZone, calendar).date;
      const key = dateKey(start);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [state.events, timeZone, calendar]);

  const range = useMemo(() => {
    if (state.view === "year") {
      // Cover the whole year on screen, plus a day of slack at each end so an
      // event that starts late on the last day of the year is not cut off.
      const first = startOfMonth({ year: state.anchor.year, month: 1, day: 1 }, calendar);
      const lastMonthLength = daysInMonth({ year: state.anchor.year, month: 12 }, calendar);
      const last = { year: state.anchor.year, month: 12, day: lastMonthLength };
      return rangeForDays([first, addDays(last, 1, calendar)], timeZone, calendar);
    }
    return visibleDays.length ? rangeForDays(visibleDays, timeZone, calendar) : null;
  }, [state.view, state.anchor.year, visibleDays, timeZone, calendar]);

  const refreshEvents = useCallback(async () => {
    if (!range) return;
    setState((s) => ({ ...s, loading: true }));
    try {
      const { items } = await api.listEvents({
        from: range.from,
        to: range.to,
        limit: 500,
        statuses: ["confirmed", "tentative", "done", "cancelled"],
      });
      setState((s) => ({ ...s, events: items, loading: false }));
    } catch {
      setState((s) => ({ ...s, loading: false }));
      toast("error.generic", "error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  useEffect(() => {
    if (state.user) void refreshEvents();
  }, [state.user, refreshEvents]);

  /**
   * Reminder delivery. The app only knows the events in the visible window, so
   * a reminder can only fire for an event that is actually on screen — the
   * exact events worth nudging about. Every scheduled offset is kept in a set
   * so an event fires once per occurrence rather than once a tick; Telegram
   * still handles reminders for events nobody is looking at.
   */
  const firedReminders = useRef(new Set<string>());
  useEffect(() => {
    if (!state.user) return;
    const fire = (): void => {
      const now = Date.now();
      for (const event of state.events) {
        if (!event.reminderMinutes || event.reminderMinutes <= 0) continue;
        const start = Date.parse(event.startUtc);
        const key = `${event.id}:${event.startUtc}:${event.reminderMinutes}`;
        if (firedReminders.current.has(key)) continue;
        if (now < start - event.reminderMinutes * 60000 || now >= start) continue;
        firedReminders.current.add(key);
        toast("reminder.fired", "ok", { title: event.title });
        desktopNotify(event.title, ui(lang, "reminder.fired") + " " + event.title);
      }
    };
    fire();
    const handle = window.setInterval(fire, 30_000);
    return () => window.clearInterval(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.user, state.events, lang]);

  const refreshCategories = useCallback(async () => {
    try {
      const { items } = await api.listCategories();
      setState((s) => ({ ...s, categories: items }));
    } catch {
      /* categories are optional decoration; a failure must not break the grid */
    }
  }, []);

  // ---- actions --------------------------------------------------------

  function toast(text: string, kind: Toast["kind"] = "ok", vars?: Record<string, string | number>) {
    const id = ++toastSeq;
    setState((s) => ({ ...s, toasts: [...s.toasts, { id, text, kind, vars }] }));
    setTimeout(() => {
      setState((s) => ({ ...s, toasts: s.toasts.filter((x) => x.id !== id) }));
    }, 4000);
  }

    function setView(view: View) {
      setState((s) => ({ ...s, view }));
    }

    /** Jump straight to a day, used when the year overview hands one over. */
    function setAnchor(anchor: CivilDate) {
      setState((s) => ({ ...s, anchor }));
    }

    /**
     * Every year the year overview offers, taken from the converters' own
     * supported ranges so a listed year is always a year the app can actually
     * convert. Sorted newest first, which is the order a dropdown of years is
     * read in.
     */
    const yearOptions = useMemo(() => {
      const [min, max] = yearRange(calendar);
      const years: number[] = [];
      for (let year = max; year >= min; year--) years.push(year);
      return years;
    }, [calendar]);

    function setYear(year: number) {
      setState((s) => {
        const [min, max] = yearRange(calendar);
        const clamped = Math.min(Math.max(year, min), max);
        // The day is carried across, so 30 Esfand must not become a day that
        // does not exist in the year picked.
        const length = daysInMonth({ year: clamped, month: s.anchor.month }, calendar);
        return { ...s, anchor: { ...s.anchor, year: clamped, day: Math.min(s.anchor.day, length) } };
      });
    }

  function goToday() {
    if (!state.user) return;
    setState((s) => ({ ...s, anchor: todayIn(s.user!.calendar, s.user!.timeZone, new Date()) }));
  }

  /** Step the anchor by whole days, clamped to the converters' range so a
   * day-either-side of a range edge never escapes into a year the grid cannot
   * draw. At the edge the anchor simply stays put. Used by the keyboard's
   * vertical arrows. */
  function stepDay(direction: 1 | -1) {
    setState((s) => {
      const next = addDays(s.anchor, direction, calendar);
      const [min, max] = yearRange(calendar);
      if (next.year < min || next.year > max) return s;
      return { ...s, anchor: next };
    });
  }

    /** Move by whole years, months or weeks, staying on the same day number. */
    function shift(direction: 1 | -1) {
      setState((s) => {
          if (s.view === "year") {
            const [min, max] = yearRange(calendar);
            const target = Math.min(Math.max(s.anchor.year + direction, min), max);
            return { ...s, anchor: { ...s.anchor, year: target } };
          }
        if (s.view === "week") {
          return { ...s, anchor: addDays(s.anchor, direction * 7, calendar) };
        }
        // Step over the whole length of the current month rather than one day,
        // and clamp to the target month's length: a 31st anchor must not
        // produce a 31 February, and the day-31 case is the reason the grid
        // used to come back empty.
        const first = startOfMonth(s.anchor, calendar);
        const step = direction === 1 ? daysInMonth(first, calendar) : -1;
        const target = addDays(first, step, calendar);
        const length = daysInMonth({ year: target.year, month: target.month }, calendar);
        return { ...s, anchor: { ...target, day: Math.min(s.anchor.day, length) } };
      });
    }

  function openEvent(id: string) {
    void (async () => {
      try {
        const event = await api.getEvent(id);
        setState((s) => ({ ...s, editing: event }));
      } catch {
        toast("error.generic", "error");
      }
    })();
  }

  function closeEditor() {
    setState((s) => ({ ...s, editing: null, editorSeed: null }));
  }

  function startNewEvent(day?: CivilDate) {
    const base = day ?? state.anchor;
    const start = civilToInstant(base, 9, 0, timeZone, calendar);
    setState((s) => ({ ...s, editing: "new", editorSeed: { startUtc: start, day: day ?? null } }));
  }

  async function saveEvent(input: Parameters<typeof api.createEvent>[0], id?: string) {
    try {
      if (id) await api.updateEvent(id, input);
      else await api.createEvent(input);
      closeEditor();
      await refreshEvents();
      toast("action.save");
    } catch {
      toast("error.generic", "error");
    }
  }

  async function removeEvent(id: string) {
    try {
      await api.deleteEvent(id);
      closeEditor();
      await refreshEvents();
      toast("action.delete");
    } catch {
      toast("error.generic", "error");
    }
  }

  async function attachFile(eventId: string, file: File) {
    try {
      await api.uploadAttachment(eventId, file);
      const event = await api.getEvent(eventId);
      setState((s) => ({
        ...s,
        events: s.events.map((e) => (e.id === eventId ? event : e)),
        editing: event,
      }));
      toast("action.attach");
    } catch {
      toast("error.generic", "error");
    }
  }

  async function setReminder(eventId: string, minutes: number) {
    try {
      const updated = await api.updateEvent(eventId, { reminderMinutes: minutes });
      setState((s) => ({
        ...s,
        events: s.events.map((e) => (e.id === eventId ? updated : e)),
        editing: updated,
      }));
      toast("action.save");
    } catch {
      toast("error.generic", "error");
    }
  }

  async function searchNotes(query: string) {
    setState((s) => ({ ...s, query }));
    if (!query.trim()) {
      setState((s) => ({ ...s, noteResults: [] }));
      return;
    }
    try {
      const { items } = await api.searchNotes(query.trim());
      setState((s) => ({ ...s, noteResults: items }));
    } catch {
      toast("error.generic", "error");
    }
  }

  // ---- .ics exchange ----------------------------------------------------

  const ICS_STATUSES: EventStatus[] = ["confirmed", "tentative", "done", "cancelled"];

  function downloadIcs(text: string) {
    const blob = new Blob([text], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = dateKey(state.anchor) + ".ics";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  /** Pull every event the account owns, in pages, and hand it to the window as
   * a single .ics download. */
  async function exportIcs() {
    try {
      const all: CalendarEvent[] = [];
      const limit = 500;
      for (let offset = 0; ; offset += limit) {
        const page = await api.listEvents({ limit, offset, statuses: ICS_STATUSES });
        all.push(...page.items);
        if (page.items.length < limit || all.length >= page.total) break;
      }
      downloadIcs(eventsToIcs(all));
      toast("ics.exported", "ok", { n: all.length });
    } catch {
      toast("error.generic", "error");
    }
  }

  /** Read an uploaded .ics and create one event per VEVENT. Recurrence rules
   * are skipped (each imported event is a single occurrence); a toast reports
   * how many landed so partial files are not silently truncated. */
  async function importIcs(file: File) {
    try {
      const parsed = parseIcs(await file.text(), timeZone);
      if (!parsed.length) {
        toast("ics.empty", "error");
        return;
      }
      let created = 0;
      for (const p of parsed.slice(0, 500)) {
        try {
          await api.createEvent({
            title: p.title,
            notes: p.notes,
            location: p.location,
            startUtc: p.startUtc,
            endUtc: p.endUtc ?? (p.allDay ? p.startUtc : new Date(Date.parse(p.startUtc) + 3_600_000).toISOString()),
            allDay: p.allDay,
            timeZone,
            status: p.status,
            reminderMinutes: 0,
          });
          created++;
        } catch {
          /* a bad VEVENT must not abort the rest of the import */
        }
      }
      await refreshEvents();
      if (created === 0) toast("ics.failed", "error");
      else if (created < parsed.length) toast("ics.partial", "ok", { n: created });
      else toast("ics.imported", "ok", { n: created });
    } catch {
      toast("error.generic", "error");
    }
  }

  async function updateUser(patch: Partial<Pick<User, "lang" | "timeZone" | "calendar" | "displayName">>) {
    try {
      const { user } = await api.updatePreferences(patch);
      applyLanguage(user.lang);
      setState((s) => {
        // Switching the active calendar does not mean jumping to today: keep
        // the same day on screen, expressed in the new system. Reinterpreting
        // the anchor as a year of the new calendar would put it out of the
        // converters' supported range (a Jalali 1405 becomes Gregorian 1405,
        // which is far below 1799) and crash the year view.
        const changed = patch.calendar && patch.calendar !== s.user?.calendar;
        if (!changed) return { ...s, user };
        const instant = civilToInstant(
          s.anchor,
          12,
          0,
          s.user?.timeZone ?? "Asia/Tehran",
          s.user?.calendar ?? "jalali",
        );
        const anchor = instantToCivil(instant, user.timeZone, user.calendar).date;
        return { ...s, user, anchor };
      });
    } catch {
      toast("error.generic", "error");
    }
  }

  async function linkAccount(apiKey: string, code: string) {
    const user = await api.claim(apiKey, code);
    setState((s) => ({
      ...s,
      user,
      anchor: todayIn(user.calendar, user.timeZone, new Date()),
      ready: true,
    }));
    await Promise.all([refreshCategories(), refreshEvents()]);
  }

  function signOut() {
    clearCredentials();
    setState((s) => ({ ...s, user: null, events: [], editing: null, view: "year" }));
  }

  return {
    state,
    user: state.user,
    categories: state.categories,
    timeZone,
    calendar,
    lang,
    visibleDays,
    eventCounts,
    t: (key: string, vars?: Record<string, string | number>) => ui(lang, key, vars),
    toast,
    setView,
    setAnchor,
    setYear,
    yearOptions,
    goToday,
    shift,
    stepDay,
    openEvent,
    closeEditor,
    startNewEvent,
    saveEvent,
    removeEvent,
    attachFile,
    setReminder,
    searchNotes,
    updateUser,
    exportIcs,
    importIcs,
    refreshCategories,
    linkAccount,
    signOut,
  };
}

export type App = ReturnType<typeof useApp>;
